export async function validateVehicleCertificate(axiosInstance, licensePlate, options = {}) {
  if (!axiosInstance || !licensePlate) {
    return { shouldAlert: false, documents: [], data: null };
  }

  try {
    const response = await axiosInstance.get(`/Vehicles/ValidateVehicule/${licensePlate}`);
    const data = response.data || {};

    // Si el vehículo no está certificado, permitir la selección sin validación
    if (!data.certificate) {
      return { shouldAlert: false, documents: [], data };
    }

    const documents = Array.isArray(data.invalidDocuments) ? data.invalidDocuments : [];
    if (documents.length === 0) {
      return { shouldAlert: false, documents: [], data };
    }

    const today = new Date();
    const expiredDocs = documents.filter(d => {
      if (!d) return false;
      if (!d.expirationDate) return false;
      const exp = new Date(d.expirationDate);
      return !isNaN(exp.getTime()) && exp < today;
    });
    const docsWithoutDate = documents.filter(d => d && !d.expirationDate);

    let docsToAlert = [...expiredDocs, ...docsWithoutDate];

    // Si no hay documentos vencidos/faltantes inicialmente, retornar válido
    if (docsToAlert.length === 0) {
      return { shouldAlert: false, documents: [], data };
    }

    const idMonitoringOrder = options.idMonitoringOrder;
    if (idMonitoringOrder) {
      try {
        const res = await axiosInstance.get(`/Travels/GetCommodity/${idMonitoringOrder}`);
        const commodities = Array.isArray(res.data) ? res.data : [];

        // Si no tiene tipos de mercancía declarados, permitir selección
        if (commodities.length === 0) {
          return { shouldAlert: false, documents: [], data };
        }

        const requiredSet = new Set();
        let hasRestrictedCommodity = false;

        commodities
          .map(c => normalizeText(c))
          .forEach(desc => {
            const reqs = requiredDocsByCommodity(desc);
            if (reqs.length > 0) {
              hasRestrictedCommodity = true;
              reqs.forEach(k => requiredSet.add(k));
            }
          });

        // Si el tipo de mercancía no corresponde a los solicitados (lista restringida), permitir selección
        if (!hasRestrictedCommodity) {
          return { shouldAlert: false, documents: [], data };
        }

        if (requiredSet.size > 0) {
          const requiredKeys = Array.from(requiredSet);
          docsToAlert = docsToAlert.filter(d => {
            const key = normalizeDocKey(d?.description);
            return requiredKeys.includes(key);
          });
        }
      } catch (_) {
        // Si hay error al obtener mercancía, asumir comportamiento permisivo
        return { shouldAlert: false, documents: [], data };
      }
    }

    const shouldAlert = docsToAlert.length > 0;
    return { shouldAlert, documents: docsToAlert, data };
  } catch (e) {
    return { shouldAlert: false, documents: [], data: null, error: e };
  }
}

function normalizeText(str) {
  const s = String(str || '').toUpperCase();
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeDocKey(desc) {
  const d = String(desc || '').toUpperCase();
  if (/SOAT/.test(d)) return 'SOAT';
  if (/TECHNICALREVIEW|TECNICOMECA/.test(d)) return 'TECHNICAL_REVIEW';
  if (/RCEPOLICY|RCE/.test(d)) return 'RCE_POLICY';
  if (/THERMOCERTIFICATE|TERMO/.test(d)) return 'THERMO_CERTIFICATE';
  if (/PREVENTIVEPEST|PEST/.test(d)) return 'PREVENTIVE_PEST';
  if (/HEALTHREGISTRY.*CARNIC|CARNIC/.test(d)) return 'HEALTH_REGISTRY_CARNIC';
  if (/HEALTHREGISTRY.*LACTIC|LACTIC/.test(d)) return 'HEALTH_REGISTRY_LACTIC';
  return d;
}

function requiredDocsByCommodity(commodityDesc) {
  // Map grupos por tipo de mercancía
  const isDry = /SECA|SECOS|MATERIAL DE EMBALAJE|EMBALAJE|MP CONCENTRADOS/.test(commodityDesc);
  const isLiquid = /LIQUIDA|LIQUIDOS|DESECHOS/.test(commodityDesc);
  const isLivestock = /GANADO EN PIE/.test(commodityDesc);
  const isCold = /REFRIGERADA|CONGELADA/.test(commodityDesc);

  if (isDry) {
    return [
      'SOAT',
      'TECHNICAL_REVIEW',
      'RCE_POLICY',
      'HEALTH_REGISTRY_CARNIC',
      'HEALTH_REGISTRY_LACTIC',
      'PREVENTIVE_PEST',
    ];
  }
  if (isLiquid) {
    return [
      'SOAT',
      'TECHNICAL_REVIEW',
      'RCE_POLICY',
    ];
  }
  if (isLivestock) {
    return [
      'SOAT',
      'TECHNICAL_REVIEW',
      'RCE_POLICY',
      'PREVENTIVE_PEST',
    ];
  }
  if (isCold) {
    return [
      'TECHNICAL_REVIEW',
      'RCE_POLICY',
      'HEALTH_REGISTRY_CARNIC',
      'HEALTH_REGISTRY_LACTIC',
      'PREVENTIVE_PEST',
      'THERMO_CERTIFICATE',
      'SOAT',
    ];
  }
  // Si no se detecta tipo, devolver array vacío (no requiere validación especial)
  return [];
}
